import { lazy, Suspense } from 'react'
import { createBrowserRouter, createRoutesFromElements, Route, useOutlet } from 'react-router-dom'
import Welcome from './pages/auth/Welcome'
import Login from './pages/auth/Login'
import SignUp from './pages/auth/SignUp'
import Activation from './pages/auth/Activation'
import SSOCallback from './pages/auth/SSOCallback'
import SAML2Callback from './pages/auth/SAML2Callback'
import Dashboard from './pages/dashboard'
import StudentsList from './pages/dashboard/admin/studentsList'
import UserDashboardSetting from './pages/UserDashboard/settings/settings'
import UserAccountDetails from './pages/UserDashboard/settings/accountDetails'
import StripeCheckoutPage from './components/stripe/StripeCheckoutPage'
import PaymentSuccessPage from './components/stripe/PaymentSuccessPage'
import UserCourse from './pages/UserDashboard/course'
import { Instructions } from './pages/UserDashboard/instructions'
import { AuthProvider } from './hooks/useAuth'
import { ProtectedRoute } from './components/global/protectedRoute'
import { AuthContextProvider } from './contexts/AuthContext'
import { HomeLayout } from './components/global/HomeLayout'
import { Completed, UserDashboard } from './pages'
import ModuleReplay from './pages/UserDashboard/moduleReplay'
import PreSurveyPage from './pages/PreSurveyPage'
import PostSurveyPage from './pages/PostSurveyPage'
import Congrats from './pages/Congrats/Congrats'
import Post90SurveyPage from './pages/Post90SurveyPage'
import NaloxoneSurveyPage from './pages/NaloxoneSurveyPage'
import Terms from './pages/auth/Terms'
import PasswordReset from './pages/auth/PasswordReset'
import AddStudents from './pages/dashboard/admin/addStudents'
import { KeyboardControlsProvider } from './contexts/KeyboardControlsContext'
import { BackgroundMusicProvider } from './contexts/BackgroundMusicContext'
import { NetworkErrorHandlerProvider } from './contexts/NetworkErrorContext'
import SSOProvider from './components/sso/SSOProvider'
import { UserPreferencesProvider } from './contexts/UserPreferencesContext'
import { LoadingSpinner } from './components/atoms'
import { isCertificateDevToolAuthRequired, isCertificateDevToolEnabled } from './utils/certificate'

// Loaded on demand so the dev-only certificate tool is not part of the production bundle.
const DevCertificatePage = lazy(async () => await import('./pages/DevCertificate/DevCertificatePage'))

const devCertificateElement = (
  <Suspense fallback={<LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />}>
    <DevCertificatePage />
  </Suspense>
)

const WrappedLayout = () => {
  const outlet = useOutlet()

  return (
    <SSOProvider>
      <AuthContextProvider>
        <AuthProvider>
          <UserPreferencesProvider>
            <KeyboardControlsProvider>
              <NetworkErrorHandlerProvider>
                <BackgroundMusicProvider>
                  {outlet}
                </BackgroundMusicProvider>
              </NetworkErrorHandlerProvider>
            </KeyboardControlsProvider>
          </UserPreferencesProvider>
        </AuthProvider>
      </AuthContextProvider>
    </SSOProvider>
  )
}

export default createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={<WrappedLayout />}
    >
      {/* Payment success page needs scrolling, so it's outside HomeLayout */}
      <Route path='/payment-success' element={<PaymentSuccessPage />} />
      {isCertificateDevToolEnabled() && !isCertificateDevToolAuthRequired() && (
        <Route path='/dev/certificate' element={devCertificateElement} />
      )}
      {/* No sign-in via QR code, so outside HomeLayout (redirects signed-in users) and ProtectedRoute (mobile-blocked) */}
      <Route path='/naloxone' element={<NaloxoneSurveyPage />} />
      <Route element={<HomeLayout />}>
        <Route path='/' element={<Welcome />} />
        <Route path='/login' element={<Login />} />
        <Route path='/signup' element={<SignUp />} />
        <Route path='/email/activation/:code' element={<Activation />} />
        <Route path='/auth/sso/callback' element={<SSOCallback />} />
        <Route path='/auth/saml/callback' element={<SAML2Callback />} />
        <Route path='/terms-of-use' element={<Terms />} />
        <Route path='/password-reset' element={<PasswordReset />} />
        <Route path='/checkout' element={<StripeCheckoutPage />} />

      </Route>
      <Route element={<ProtectedRoute />}>
        {isCertificateDevToolAuthRequired() && (
          <Route path='/dev/certificate' element={devCertificateElement} />
        )}
        <Route path='/presurvey' element={<PreSurveyPage />} />
        <Route path='/postsurvey' element={<PostSurveyPage />} />
        <Route path='/90postsurvey' element={<Post90SurveyPage />} />
        <Route path='/naloxone-survey' element={<NaloxoneSurveyPage authenticated />} />
        <Route path='/user-dashboard/setting' element={<UserDashboardSetting />}>
          <Route path='account-details' element={<UserAccountDetails />} />
        </Route>
        <Route path='/user-dashboard/instructions' element={<Instructions />} />
        <Route path='/user-dashboard/courses/:id' element={<UserCourse />} />
        <Route path='/user-dashboard/congrats' element={<Congrats />} />
        <Route path='/user-dashboard/completed' element={<Completed />} />
        <Route path='/user-dashboard' element={<UserDashboard />} />
        <Route path='/dashboard' element={<Dashboard />}>
          <Route path='account-details' element={<UserAccountDetails />} />
          <Route path=':pageType' element={<StudentsList />} />
          <Route path='add-students' element={<AddStudents />} />
        </Route>
        <Route path='/user-dashboard/module-replay' element={<ModuleReplay />} />
      </Route>
    </Route>
  )
)
